import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link2, Check, Plus, Pencil, Trash2, AlertCircle, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { VariableMapping } from "@/types/supabase";

interface PingPostProvider {
  chatbot_id?: string | null;
  id: string;
  name: string;
  url: string;
  secret_key?: string | null;
  user_id: string;
  variable_mappings: VariableMapping[];
  is_enabled?: boolean;
  is_global?: boolean;
  created_at?: string;
}

interface FieldDictionary {
  id: string;
  key: string;
  friendly_name: string;
}

// URL validation function that allows query parameters
const isValidUrl = (url: string): boolean => {
  if (!url) return false; // Empty URLs are invalid for providers

  // Trim and check for spaces
  const trimmedUrl = url.trim();
  if (trimmedUrl !== url || trimmedUrl.includes(' ')) {
    return false;
  }

  try {
    // Use URL constructor for validation
    const urlObj = new URL(url);

    // Verify protocol is http or https
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return false;
    }

    const hostname = urlObj.hostname;

    // Make sure hostname doesn't have spaces
    if (hostname.includes(' ')) {
      return false;
    }

    // Check for double dots (invalid domain)
    if (hostname.includes('..')) {
      return false;
    }

    // Allow localhost and IP addresses for development
    if (hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        /^192\.168\./.test(hostname) ||
        /^10\./.test(hostname) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)) {
      return true;
    }

    // For other hostnames, ensure they have at least one dot (valid domain)
    if (!hostname.includes('.')) {
      return false;
    }

    return true;
  } catch (error) {
    // If URL constructor throws an error, the URL is invalid
    return false;
  }
};

export const GlobalIntegrations = ({ providedUser }) => {
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [providers, setProviders] = useState<PingPostProvider[]>([]);
  const [currentProvider, setCurrentProvider] = useState<PingPostProvider | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteProviderId, setDeleteProviderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fieldsDictionary, setFieldsDictionary] = useState<FieldDictionary[]>([]);
  const [isFieldsLoading, setIsFieldsLoading] = useState(false);
  const [fieldSearchMap, setFieldSearchMap] = useState<Record<number, string>>({});
  const fieldSearchRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const user = providedUser ?? authUser;

  useEffect(() => {
    loadFieldsDictionary();
  }, []);

  useEffect(() => {
    if (user) {
      loadProviders();
    }
  }, [user]);

  const loadFieldsDictionary = async () => {
    setIsFieldsLoading(true);
    try {
      const { data, error } = await supabase
        .from('fields_dictionary')
        .select('id, key, friendly_name');

      if (error) throw error;

      setFieldsDictionary(data || []);
    } catch (error: any) {
      console.error('Error loading fields dictionary:', error);
      toast({
        variant: 'destructive',
        title: 'Error loading fields',
        description: error.message
      });
    } finally {
      setIsFieldsLoading(false);
    }
  };

  const loadProviders = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('pingpost_providers')
        .select('*')
        .eq('user_id', user?.id);

      // Check if the error is due to the table not existing
      if (error && error.code === '42P01') { // PostgreSQL code for undefined_table
        console.log('The pingpost_providers table does not exist yet. Migration needs to be applied.');
        setProviders([]);
        setIsLoading(false);
        return;
      }

      if (error) throw error;

      setProviders((data || []).map(provider => ({
        ...provider,
        variable_mappings: Array.isArray(provider.variable_mappings) ? provider.variable_mappings : [],
        secret_key: provider.secret_key ?? ''
      })));
    } catch (error: any) {
      console.error('Error loading pingpost providers:', error);
      toast({
        variant: 'destructive',
        title: 'Error loading providers',
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addVariableMapping = () => {
    setCurrentProvider(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        variable_mappings: [
          ...(prev.variable_mappings || []),
          { key_dictionary_id: '', post_name: '' }
        ]
      };
    });
  };

  const updateVariableMapping = (
    index: number,
    field: keyof VariableMapping,
    value: string
  ) => {
    setCurrentProvider(prev => {
      if (!prev) return prev;
      const variable_mappings = [...(prev.variable_mappings || [])];
      variable_mappings[index] = {
        ...variable_mappings[index],
        [field]: value
      };
      if (field === 'key_dictionary_id') {
        const selectedField = fieldsDictionary.find(fd => fd.id === value);
        if (selectedField) {
          variable_mappings[index].post_name = selectedField.key;
        }
      }
      return { ...prev, variable_mappings };
    });
  };

  const removeVariableMapping = (index: number) => {
    setCurrentProvider(prev => {
      if (!prev) return prev;
      const variable_mappings = (prev.variable_mappings || []).filter((_, i) => i !== index);
      return { ...prev, variable_mappings };
    });
  };

  const isFieldDisabled = (currentIndex: number, optionId: string) => {
    return (currentProvider?.variable_mappings || []).some(
      (mapping, idx) => idx !== currentIndex && mapping.key_dictionary_id?.trim() === optionId
    );
  };

  const getSortedFieldsForSelect = (currentIndex: number) => {
    const term = (fieldSearchMap[currentIndex] || '').trim().toLowerCase();
    const currentId = currentProvider?.variable_mappings?.[currentIndex]?.key_dictionary_id?.trim();

    const filtered = term
      ? fieldsDictionary.filter(fd =>
          fd.friendly_name.toLowerCase().includes(term) ||
          fd.key.toLowerCase().includes(term)
        )
      : fieldsDictionary;

    let list = [...filtered];

    if (currentId && !list.some(fd => fd.id === currentId)) {
      const existing = fieldsDictionary.find(fd => fd.id === currentId);
      list = existing ? [existing, ...list] : [{ id: currentId, friendly_name: currentId, key: currentId } as FieldDictionary, ...list];
    }

    return list.sort((a, b) => {
      if (currentId) {
        if (a.id === currentId && b.id !== currentId) return -1;
        if (b.id === currentId && a.id !== currentId) return 1;
      }
      const aDisabled = isFieldDisabled(currentIndex, a.id);
      const bDisabled = isFieldDisabled(currentIndex, b.id);
      return Number(aDisabled) - Number(bDisabled);
    });
  };

  const handleAddProvider = () => {
    setCurrentProvider({
      id: crypto.randomUUID(),
      name: '',
      url: '',
      secret_key: '',
      user_id: user?.id || '',
      variable_mappings: []
    });
    setIsEditing(false);
    setShowConfigModal(true);
  };

  const handleEditProvider = (provider: PingPostProvider) => {
    setCurrentProvider({
      ...provider,
      variable_mappings: Array.isArray(provider.variable_mappings) ? provider.variable_mappings : []
    });
    setIsEditing(true);
    setShowConfigModal(true);
  };

  const handleDeleteProvider = (id: string) => {
    setDeleteProviderId(id);
  };

  const confirmDeleteProvider = async () => {
    if (deleteProviderId) {
      try {
        const { error } = await supabase
          .from('pingpost_providers')
          .delete()
          .eq('id', deleteProviderId);

        if (error) throw error;

        setProviders(providers.filter(p => p.id !== deleteProviderId));

        toast({
          title: 'Provider deleted',
          description: 'The pingpost provider has been removed'
        });
      } catch (error: any) {
        toast({
          variant: 'destructive',
          title: 'Error deleting provider',
          description: error.message
        });
      } finally {
        setDeleteProviderId(null);
      }
    }
  };

  const handleSaveConfig = async () => {
    if (!currentProvider || !user) return;

    if (!currentProvider.name.trim()) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Provider name is required'
      });
      return;
    }

    if (!isValidUrl(currentProvider.url)) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Please enter a valid URL'
      });
      return;
    }

    const sanitizedMappings = (currentProvider.variable_mappings || []).map(mapping => ({
      key_dictionary_id: mapping?.key_dictionary_id || '',
      post_name: (mapping?.post_name || '').trim()
    }));

    if (
      !sanitizedMappings.length ||
      sanitizedMappings.some(mapping => !mapping.key_dictionary_id || !mapping.post_name)
    ) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'At least one variable mapping with a field and post name is required'
      });
      return;
    }

    const normalizedSecretKey = currentProvider.secret_key?.trim() || null;

    try {
      if (isEditing) {
        // Update existing provider
        const { error } = await supabase
          .from('pingpost_providers')
          .update({
            name: currentProvider.name,
            url: currentProvider.url,
            secret_key: normalizedSecretKey,
            variable_mappings: sanitizedMappings
          })
          .eq('id', currentProvider.id);

        if (error) throw error;

        setProviders(providers.map(p =>
          p.id === currentProvider.id
            ? { ...currentProvider, secret_key: normalizedSecretKey, variable_mappings: sanitizedMappings }
            : p
        ));

        toast({
          title: 'Provider updated',
          description: 'The pingpost provider has been updated successfully'
        });
      } else {
        // Add new provider
        const { error } = await supabase
          .from('pingpost_providers')
          .insert([{
            id: currentProvider.id,
            name: currentProvider.name,
            url: currentProvider.url,
            secret_key: normalizedSecretKey,
            user_id: user.id,
            variable_mappings: sanitizedMappings
          }]);

        if (error) throw error;

        setProviders([...providers, { ...currentProvider, secret_key: normalizedSecretKey, variable_mappings: sanitizedMappings }]);

        toast({
          title: 'Provider added',
          description: 'The pingpost provider has been added successfully'
        });
      }

      setShowConfigModal(false);
      setCurrentProvider(null);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error saving provider',
        description: error.message
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold">Webhook Integrations</h4>
                <p className="text-sm text-gray-600">
                  Configure Webhook Integrations to send lead data to multiple external endpoints.
                  These providers can be enabled or disabled for each chatbot.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {isLoading ? (
                <div className="text-sm text-gray-600">Loading providers...</div>
              ) : providers.length === 0 ? (
                <div className="space-y-2">
                  <div className="text-sm text-gray-600">No providers configured yet.</div>
                </div>
              ) : (
                providers.map(provider => (
                  <div
                    key={provider.id}
                    className="flex items-center justify-between p-3 border rounded-md"
                  >
                    <div className="space-y-1">
                      <div className="font-medium flex items-center gap-2">
                        {provider.name}
                        <Badge variant="outline" className="text-xs">
                          Global
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <Link2 className="h-3 w-3" />
                        {provider.url}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditProvider(provider)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteProvider(provider.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleAddProvider}
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Webhook Integration
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Dialog
        open={showConfigModal}
        onOpenChange={(open) => {
          setShowConfigModal(open);
          if (!open) setCurrentProvider(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit' : 'Add'} Webhook Integration</DialogTitle>
            <DialogDescription>
              Configure a Webhook Integration to send lead data to external endpoints.
              This provider can be enabled or disabled for each chatbot.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 px-1 overflow-y-auto max-h-[60vh]">
            <div className="space-y-2">
              <Label htmlFor="name">Provider Name</Label>
              <Input
                id="name"
                placeholder="My Lead Provider"
                value={currentProvider?.name || ''}
                onChange={(e) => setCurrentProvider(prev =>
                  prev ? {...prev, name: e.target.value} : null
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">Endpoint URL</Label>
              <div className="relative">
                <Input
                  id="url"
                  placeholder="https://your-endpoint.com/api"
                  value={currentProvider?.url || ''}
                  onChange={(e) => setCurrentProvider(prev =>
                    prev ? {...prev, url: e.target.value} : null
                  )}
                  autoComplete="off"
                  name="pingpost-url"
                  className={currentProvider?.url && !isValidUrl(currentProvider.url) ? 'border-red-300 pr-10' : ''}
                />
                {currentProvider?.url && !isValidUrl(currentProvider.url) && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  </div>
                )}
              </div>
              {currentProvider?.url && !isValidUrl(currentProvider.url) && (
                <p className="text-xs text-red-500 mt-1">
                  Please enter a valid URL (must start with http:// or https://)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="secretKey">Secret Key</Label>
              <Input
                id="secretKey"
                placeholder="Enter your secret key (optional)"
                value={currentProvider?.secret_key || ''}
                onChange={(e) => setCurrentProvider(prev =>
                  prev ? {...prev, secret_key: e.target.value} : null
                )}
                autoComplete="off"
                name="pingpost-secret"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Variable Mappings</Label>
              </div>
              <p className="text-xs text-gray-600">
                Map fields from dictionary to the names expected by the endpoint.
              </p>

              {isFieldsLoading && (
                <div className="text-sm text-gray-600">Loading available fields...</div>
              )}

              {!isFieldsLoading && fieldsDictionary.length === 0 && (
                <div className="text-sm text-red-500">
                  No fields found in the dictionary. Add fields before creating mappings.
                </div>
              )}

              <div className="space-y-3">
                {(currentProvider?.variable_mappings || []).map((mapping, index) => (
                  <div
                    key={`${mapping.key_dictionary_id}-${index}`}
                    className="rounded-md border p-3"
                  >
                    <div className="flex flex-col md:flex-row md:items-end gap-3">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs text-gray-600">Field Name</Label>
                        <Select
                          value={mapping.key_dictionary_id}
                          onValueChange={(value) => updateVariableMapping(index, 'key_dictionary_id', value)}
                          disabled={fieldsDictionary.length === 0}
                          onOpenChange={(open) => {
                            if (open) {
                              setFieldSearchMap(prev => ({ ...prev, [index]: '' }));
                              setTimeout(() => {
                                fieldSearchRefs.current[index]?.focus();
                              }, 0);
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a field" />
                          </SelectTrigger>
                          <SelectContent>
                            <div className="px-2 pb-2">
                              <Input
                                placeholder="Search field..."
                                value={fieldSearchMap[index] || ''}
                                onChange={(e) => setFieldSearchMap(prev => ({ ...prev, [index]: e.target.value }))}
                                ref={(el) => { fieldSearchRefs.current[index] = el; }}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                            {(() => {
                              const options = getSortedFieldsForSelect(index);
                              if (options.length === 0) {
                                return (
                                  <SelectItem key="no-results" value="no-results" disabled>
                                    No fields match
                                  </SelectItem>
                                );
                              }
                              return options.map(field => (
                                <SelectItem
                                  key={field.id}
                                  value={field.id}
                                  disabled={isFieldDisabled(index, field.id)}
                                >
                                  {field.friendly_name || field.key}
                                </SelectItem>
                              ));
                            })()}
                            {mapping.key_dictionary_id &&
                              !getSortedFieldsForSelect(index).some(fd => fd.id === mapping.key_dictionary_id) && (
                                <SelectItem
                                  key={`current-${mapping.key_dictionary_id}`}
                                  value={mapping.key_dictionary_id}
                                  disabled={isFieldDisabled(index, mapping.key_dictionary_id)}
                                >
                                  {mapping.key_dictionary_id}
                                </SelectItem>
                              )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex-1 space-y-1">
                        <Label className="text-xs text-gray-600">POST Variable Name</Label>
                        <Input
                          placeholder="e.g. first_name"
                          value={mapping.post_name}
                          onChange={(e) => updateVariableMapping(index, 'post_name', e.target.value)}
                        />
                      </div>

                      <div className="flex items-center justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVariableMapping(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

                {(currentProvider?.variable_mappings || []).length === 0 && (
                  <div className="text-sm text-gray-600">
                    Add at least one mapping to continue.
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addVariableMapping}
                    disabled={(fieldsDictionary.length === 0 && !isFieldsLoading) || !currentProvider}
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add Variable
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig}>Save Provider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteProviderId} onOpenChange={(open) => !open && setDeleteProviderId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this Webhook Integration. This action cannot be undone.
              Any chatbots using this provider will no longer be able to send data to this endpoint.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteProvider}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
